//@ts-nocheck

import { Action, ActionReducer } from '@ngrx/store';
import { RootState } from '@store/index';

declare var console;
const logger = console;
const INIT_ACTION = '@ngrx/store/init';

export interface LoggerOptions {
  /**
   * 'log' | 'console' | 'warn' | 'error' | 'info'. Default: 'log'
   */
  level?: 'log' | 'console' | 'warn' | 'error' | 'info';
  collapsed?: boolean;
  duration?: boolean;
  timestamp?: boolean;
  filter?: LoggerFilterOption;
  stateTransformer?: (state: object) => object;
  actionTransformer?: (action: object) => object;
  colors?: LoggerColorsOption;
}

export interface LoggerFilterOption {
  whitelist?: string[];
  blacklist?: string[];
}

export interface LoggerBuffer {
  action: Action;
  nextState: RootState;
  prevState: any;
}

export interface LoggerColorsOption {
  title: (action: object) => string;
  prevState: (prevState: object) => string;
  action: (action: object) => string;
  nextState: (nextState: object) => string;
  error: (error: any, prevState: object) => string;
}

export class StoreLogger {
  static isServer = typeof document === 'undefined';

  static defaultLoggerOptions: LoggerOptions = {
    level: 'log',
    collapsed: false,
    duration: true,
    timestamp: true,
    stateTransformer: state => state,
    actionTransformer: action => action,
    filter: { whitelist: [], blacklist: [] },
    colors: {
      title: null,
      prevState: () => '#9E9E9E',
      action: () => '#03A9F4',
      nextState: () => '#4CAF50',
      error: () => '#F20404',
    },
  };

  static logger(loggerOptions: LoggerOptions = {}) {
    if (this.isServer) {
      return reducer => reducer;
    }

    return (reducer: ActionReducer<RootState>) => {
      let log = {};

      const options = { ...this.defaultLoggerOptions, ...loggerOptions };
      const { stateTransformer } = options;
      const buffer = this.printBuffer(options);

      return (state, action) => {
        const preLog = {
          started: performance.now(),
          startedTime: new Date(),
          prevState: stateTransformer(log),
          action,
        };

        const nextState = reducer(state, action);

        const postLog = {
          took: performance.now() - preLog.started,
          nextState: stateTransformer(nextState),
        };

        log = { ...preLog, ...postLog };
        //ignore init action fired by store and devtools
        if (action.type !== INIT_ACTION && this.isAllowed(action, options.filter)) {
          buffer([log]);
        }

        return nextState;
      };
    };
  }

  static formatTime(time: Date) {
    return [
      '@',
      this.pad(time.getHours()),
      this.pad(time.getMinutes()),
      this.pad(time.getSeconds()),
      this.pad(time.getMilliseconds()),
    ].join(',');
  }

  static pad(number: number, maxLength = 2) {
    return number.toString().padStart(2, '0');
  }

  static getLogLevel(level: unknown, action, payload, type): any {
    switch (typeof level) {
      case `object`:
        return typeof level[type] === `function` ? level[type](...payload) : level[type];
      case `function`:
        return level(action);
      default:
        return level;
    }
  }

  static printBuffer(options: LoggerOptions): () => void {
    return logBuffer => {
      const { actionTransformer, collapsed, colors, timestamp, duration, level } = options;

      logBuffer.forEach((logEntry, key) => {
        const { started, startedTime, action, error } = logEntry;
        const prevState = logEntry.prevState.nextState ?? '(Empty)';
        let { took, nextState } = logEntry;
        const nextEntry = logBuffer[key + 1];

        if (nextEntry) {
          nextState = nextEntry.prevState;
          took = nextEntry.started - started;
        }

        const formattedAction = actionTransformer(action);
        const isCollapsed = typeof collapsed === `function` ? collapsed(() => nextState, action) : collapsed;
        const formattedTime = this.formatTime(startedTime);
        const titleCSS = colors.title ? `color: ${colors.title(formattedAction)};` : null;
        const title = `action ${timestamp ? formattedTime : ``} ${formattedAction.type} ${
          duration ? `(in ${took?.toFixed(2)} ms)` : ``
        }`;

        try {
          if (isCollapsed) {
            if (colors.title) logger.groupCollapsed(`%c ${title}`, titleCSS);
            else logger.groupCollapsed(title);
          } else {
            if (colors.title) logger.group(`%c ${title}`, titleCSS);
            else logger.group(title);
          }
        } catch (e) {
          logger.log(title);
        }

        const prevStateLevel = this.getLogLevel(level, formattedAction, [prevState], `prevState`);
        const actionLevel = this.getLogLevel(level, formattedAction, [formattedAction], `action`);
        const errorLevel = this.getLogLevel(level, formattedAction, [error, prevState], `error`);
        const nextStateLevel = this.getLogLevel(level, formattedAction, [nextState], `nextState`);

        if (prevStateLevel) {
          if (colors.prevState)
            logger[prevStateLevel](`%c prev state`, `color: ${colors.prevState(prevState)}; font-weight: bold`, prevState);
          else logger[prevStateLevel](`prev state`, prevState);
        }

        if (actionLevel) {
          if (colors.action)
            logger[actionLevel](`%c action`, `color: ${colors.action(formattedAction)}; font-weight: bold`, formattedAction);
          else logger[actionLevel](`action`, formattedAction);
        }

        if (error && errorLevel) {
          if (colors.error) logger[errorLevel](`%c error`, `color: ${colors.error(error, prevState)}; font-weight: bold`, error);
          else logger[errorLevel](`error`, error);
        }

        if (nextStateLevel) {
          if (colors.nextState)
            logger[nextStateLevel](`%c next state`, `color: ${colors.nextState(nextState)}; font-weight: bold`, nextState);
          else logger[nextStateLevel](`next state`, nextState);
        }

        try {
          logger.groupEnd();
        } catch (e) {
          logger.log(`—— log end ——`);
        }
      });

      logBuffer.length = 0;
    };
  }

  static isAllowed = (action: any, filter: any): boolean => {
    if (!filter) {
      return true;
    }

    if (filter.whitelist && filter.whitelist.length) {
      return filter.whitelist.indexOf(action.type) !== -1;
    }

    return filter.blacklist && filter.blacklist.indexOf(action.type) === -1;
  };
}
